import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { CheckCircle2, AlertCircle, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EXT = ["pdf", "doc", "docx"];
const MAX_BYTES = 5 * 1024 * 1024;

const optionalHttps = z
  .string()
  .trim()
  .max(300)
  .refine((v) => !v || v.startsWith("https://"), "Link must start with https://")
  .optional()
  .or(z.literal(""));

const schema = z.object({
  fullName: z.string().trim().min(2, "Please enter your full name."),
  email: z.string().trim().email("Please enter a valid email address."),
  phone: z.string().trim().regex(/^[+0-9][0-9\s-]{5,15}$/, "Please enter a valid phone number."),
  currentLocation: z.string().trim().min(2, "Please enter your current location."),
  totalExperience: z.string().trim().min(1, "Please enter your total experience."),
  relevantExperience: z.string().trim().min(1, "Please enter your relevant experience."),
  currentCompany: z.string().trim().max(160).optional().or(z.literal("")),
  noticePeriod: z.string().trim().min(1, "Please enter your notice period."),
  linkedinUrl: optionalHttps,
  portfolioUrl: optionalHttps,
  coverMessage: z.string().trim().max(3000, "Please keep your message under 3000 characters.").optional().or(z.literal("")),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please provide your consent so we can process your application." }),
  }),
});

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="field-label">
      {label} {required && <span className="text-brand-red">*</span>}
    </label>
    {children}
    {error && (
      <p className="field-error" role="alert">
        {error.message}
      </p>
    )}
  </div>
);

const JobApplicationForm = ({ job }) => {
  const [submitted, setSubmitted] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { consent: false } });

  const onFileChange = (e) => {
    setResumeError(null);
    const file = e.target.files?.[0] || null;
    setResumeFile(file);
  };

  const onSubmit = async (data, event) => {
    setSubmitError(null);
    setResumeError(null);
    if (!resumeFile) {
      setResumeError("Please attach your résumé (PDF, DOC or DOCX).");
      return;
    }
    const ext = resumeFile.name.includes(".") ? resumeFile.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.includes(ext)) {
      setResumeError("Only PDF, DOC or DOCX résumés are accepted.");
      return;
    }
    if (resumeFile.size > MAX_BYTES) {
      setResumeError("Résumé must be smaller than 5 MB.");
      return;
    }
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, k === "consent" ? "true" : v ?? ""));
    fd.append("website", event?.target?.elements?.website?.value || "");
    fd.append("resume", resumeFile);
    try {
      await axios.post(`${API}/api/careers/jobs/${job.id}/apply`, fd);
      setSubmitted(data.fullName);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSubmitError(typeof detail === "string" ? detail : "We could not submit your application right now. Please try again shortly.");
    }
  };

  if (submitted) {
    return (
      <div className="border-t border-border bg-white p-6 sm:p-8" data-testid="application-success">
        <CheckCircle2 className="h-9 w-9 text-brand-red" aria-hidden="true" />
        <h4 className="mt-4 text-xl font-semibold">Thank you, {submitted}.</h4>
        <p className="mt-2 text-sm leading-relaxed text-brand-grey">
          Your application for <strong className="text-brand-graphite">{job.title}</strong> has been
          received. A confirmation email is on its way to you, and our recruitment team will contact
          you if your profile is shortlisted.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="border-t border-border bg-white p-6 sm:p-8" data-testid="application-form">
      <h4 className="text-lg font-semibold">Apply for {job.title}</h4>
      <p className="mt-1 text-sm text-brand-grey">
        Fields marked with <span className="text-brand-red">*</span> are required.
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" required error={errors.fullName}>
          <input className="field-input" autoComplete="name" data-testid="apply-fullname-input" {...register("fullName")} />
        </Field>
        <Field label="Email" required error={errors.email}>
          <input type="email" className="field-input" autoComplete="email" data-testid="apply-email-input" {...register("email")} />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <input type="tel" className="field-input" autoComplete="tel" placeholder="+91 98XXXXXXXX" data-testid="apply-phone-input" {...register("phone")} />
        </Field>
        <Field label="Current Location" required error={errors.currentLocation}>
          <input className="field-input" placeholder="City, Country" data-testid="apply-location-input" {...register("currentLocation")} />
        </Field>
        <Field label="Total Experience" required error={errors.totalExperience}>
          <input className="field-input" placeholder="e.g. 6 years" data-testid="apply-total-exp-input" {...register("totalExperience")} />
        </Field>
        <Field label="Relevant Experience" required error={errors.relevantExperience}>
          <input className="field-input" placeholder="e.g. 4 years" data-testid="apply-relevant-exp-input" {...register("relevantExperience")} />
        </Field>
        <Field label="Current Company" error={errors.currentCompany}>
          <input className="field-input" data-testid="apply-company-input" {...register("currentCompany")} />
        </Field>
        <Field label="Notice Period" required error={errors.noticePeriod}>
          <input className="field-input" placeholder="e.g. 30 days / Immediate" data-testid="apply-notice-input" {...register("noticePeriod")} />
        </Field>
        <Field label="LinkedIn URL" error={errors.linkedinUrl}>
          <input className="field-input" placeholder="https://linkedin.com/in/…" data-testid="apply-linkedin-input" {...register("linkedinUrl")} />
        </Field>
        <Field label="Portfolio / GitHub URL" error={errors.portfolioUrl}>
          <input className="field-input" placeholder="https://github.com/…" data-testid="apply-portfolio-input" {...register("portfolioUrl")} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Cover Message" error={errors.coverMessage}>
            <textarea rows={4} className="field-input resize-y" placeholder="Tell us briefly why you are a great fit for this role." data-testid="apply-cover-textarea" {...register("coverMessage")} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">
            Résumé (PDF, DOC or DOCX — max 5 MB) <span className="text-brand-red">*</span>
          </label>
          <label className="flex cursor-pointer items-center gap-3 border border-dashed border-border bg-brand-surface px-4 py-4 text-sm text-brand-grey transition-colors hover:border-brand-red">
            <Upload className="h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" />
            <span className="truncate">{resumeFile ? resumeFile.name : "Click to attach your résumé"}</span>
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onFileChange} data-testid="apply-resume-input" />
          </label>
          {resumeError && (
            <p className="field-error" role="alert" data-testid="apply-resume-error">
              {resumeError}
            </p>
          )}
        </div>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" defaultValue="" />
        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-brand-grey">
            <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-brand-red" data-testid="apply-consent-checkbox" {...register("consent")} />
            <span>
              I consent to Vaskion Technologies Private Limited processing my personal information
              and résumé for recruitment purposes. <span className="text-brand-red">*</span>
            </span>
          </label>
          {errors.consent && (
            <p className="field-error" role="alert" data-testid="apply-consent-error">
              {errors.consent.message}
            </p>
          )}
        </div>
      </div>
      {submitError && (
        <p className="mt-5 flex items-start gap-2 border-l-2 border-brand-red bg-brand-surface p-4 text-sm leading-relaxed text-brand-graphite" role="alert" data-testid="apply-submit-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" />
          <span>{submitError}</span>
        </p>
      )}
      <button type="submit" className="btn-primary mt-6 w-full sm:w-auto" disabled={isSubmitting} data-testid="apply-submit-button">
        {isSubmitting ? "Submitting…" : "Submit Application"}
      </button>
    </form>
  );
};

export default JobApplicationForm;
